import { lazy, Suspense, type ComponentType } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { Loading } from "components/Loading";

import { calendarRoute } from "utils/calendarRoute";

import Layout from "./Layout";
import LayoutSimple from "./LayoutSimple";
import LayoutSimpleFluid from "./LayoutSimpleFluid";
import LayoutSimpleWide from "./LayoutSimpleWide";

import Dashboard from "./Dashboard/Dashboard";

import AssignmentsRoutes from "./Assignments";
import CMSAssignmentDetailRoutes from "./CMSAssignmentDetail";
import ExerciseRoutes from "./Exercise";
import { OnboardingGuard } from "./Onboarding/OnboardingGuard";
import HistoryRoutes from "./History";
import PracticeRoutes from "./Practice";

import { MhdPractice } from "./Practice/MhdPractice";
import { MixedPractice } from "./Practice/MixedPractice";
import { PracticeResults } from "./Practice/Results";

import { CompleteCourseGuard } from "./CompleteCourse/CompleteCourseGuard";
import { ExerciseSessionArea } from "components/ExerciseSessionArea";
import TeacherEvaluationUpload from "./TeacherEvaluationUpload";
import { TutoringGuard, TutoringPracticeGuard } from "./Tutoring/TutoringGuard";
import UnrecognizedAnswersPage from "./UnrecognizedAnswers";

// AIDEV-NOTE: Doučování, cvičení, procvičování a dashboard zůstávají staticky importované
// schválně — jsou na kritické cestě k prvnímu zadání a lazy chunk by jim přidal další
// round-trip. Lazy jde jen to, co student cestou do doučování nepotká.
const BlacklistRoutes = lazy(async () => ({ default: (await import("./Blacklist")).BlacklistRoutes }));
const BookmarksRoutes = lazy(async () => ({ default: (await import("./Bookmarks")).BookmarksRoutes }));
const BooksRoutes = lazy(async () => ({ default: (await import("./Books")).BooksRoutes }));
const FaqRoutes = lazy(async () => await import("./Faq"));
const MasterclassRoutes = lazy(async () => ({ default: (await import("./Masterclass")).MasterclassRoutes }));
const OnboardingRoutes = lazy(async () => await import("./Onboarding"));
const OnboardingExercise = lazy(async () => await import("./Onboarding/Exercise"));
const OnboardingResults = lazy(async () => await import("./Onboarding/ExerciseResults"));
const SettingsRoutes = lazy(async () => await import("./Settings"));
const StatisticsRoutes = lazy(async () => await import("./Statistics"));
const VideoTipsRoutes = lazy(async () => await import("./VideoTips"));

// AIDEV-NOTE: Kalendář je lazy s přednačtením (`utils/lazyWithPreload`), ne přes `lazy`. React 19 odkryje
// obsah pozastavené lazy routy nejdřív 300 ms po zobrazení fallbacku (FALLBACK_THROTTLE_MS), i když chunk
// dorazil za 20 ms; po `preload()` se komponenta vykresluje synchronně, bez Suspense. Chunk se přednačítá
// při nečinnosti po startu, při najetí na odkaz v menu a hned při startu na /kalendar. Ostatní lazy routy
// zůstaly beze změny. [TD-476]
const CalendarRoutes = calendarRoute.Component;

// AIDEV-NOTE: Vlastní <Suspense> u každé lazy routy, ne jen ta kořenová: kořenová sedí nad
// celým <Routes>, takže by se při čekání na chunk odmountoval i Layout a stránka by na
// okamžik problikla prázdnou plochou včetně menu. Takhle se vymění jen obsah Outletu.
const lazyRoute = (Page: ComponentType) => (
  <Suspense fallback={<Loading />}>
    <Page />
  </Suspense>
);

/* DEV ROUTES START */
// AIDEV-NOTE: Vývojářské stránky visí na jediné podmínce nad `import.meta.env.VITE_ENV`.
// Vite ten výraz při buildu nahradí literálem, Rollup celou mrtvou větev i s `import()`
// vyhodí — do produkčního balíku se `./Dev/*` vůbec nedostane (dřív tam bylo ~146 kB
// zdrojů). Prahem je `!== "production"`, ne `import.meta.env.DEV`, aby dev stránky zůstaly
// dostupné i na stage buildu — stejnou podmínku má odkaz na ně v `Menu.tsx`.
const devPages =
  import.meta.env.VITE_ENV === "production"
    ? null
    : {
        Routes: lazy(async () => await import("./Dev")),
        Exercise01: lazy(async () => await import("./Dev/Excercise/Exercise01")),
        Exercise02: lazy(async () => await import("./Dev/Excercise/Exercise02")),
        Exercise03: lazy(async () => await import("./Dev/Excercise/Exercise03")),
        Exercise04: lazy(async () => await import("./Dev/Excercise/Exercise04")),
        Exercise05: lazy(async () => await import("./Dev/Excercise/Exercise05")),
        Exercise06: lazy(async () => await import("./Dev/Excercise/Exercise06")),
        Exercise07: lazy(async () => await import("./Dev/Excercise/Exercise07")),
        Exercise08: lazy(async () => await import("./Dev/Excercise/Exercise08")),
        Exercise09: lazy(async () => await import("./Dev/Excercise/Exercise09")),
        Exercise10: lazy(async () => await import("./Dev/Excercise/Exercise10")),
        Results01: lazy(async () => await import("./Dev/Results/Results01")),
        Results02: lazy(async () => await import("./Dev/Results/Results02")),
      };
/* DEV ROUTES END */

const UserRouter = () => (
  <Suspense fallback={<Loading />}>
    <Routes>
      <Route path="/*" element={<Layout />}>
        <Route index element={<Dashboard />} />
        <Route path="dotazy/*" element={lazyRoute(FaqRoutes)} />
        <Route path="kalendar/*" element={lazyRoute(CalendarRoutes)} />
        <Route path="knihy/*" element={lazyRoute(BooksRoutes)} />
        <Route path="masterclass/*" element={lazyRoute(MasterclassRoutes)} />
        <Route path="nastaveni/*" element={lazyRoute(SettingsRoutes)} />
        <Route path="statistiky/*" element={lazyRoute(StatisticsRoutes)} />
        <Route path="ulohy/*" element={<AssignmentsRoutes />} />
        <Route path="ulohy-z-cms/*" element={<CMSAssignmentDetailRoutes />} />
        <Route path="ulozene/*" element={lazyRoute(BookmarksRoutes)} />
        <Route path="videotipy/*" element={lazyRoute(VideoTipsRoutes)} />
        <Route path="vyrazene/*" element={lazyRoute(BlacklistRoutes)} />
        <Route path="doucovani/*" element={<TutoringGuard />} />
        <Route path="historie/*" element={<HistoryRoutes />} />
        <Route path="reklamovane-odpovedi" element={<UnrecognizedAnswersPage />} />
        {/* Stará cesta žije v odkazech mimo aplikaci (e-maily, Targito). [TD-334] */}
        <Route path="nahlasene-odpovedi" element={<Navigate to="/reklamovane-odpovedi" replace />} />
        <Route path="kompletni-kurz" element={<CompleteCourseGuard />} />
      </Route>

      <Route path="/*" element={<LayoutSimpleWide />}>
        <Route path="onboarding/priklad" element={<OnboardingGuard>{lazyRoute(OnboardingExercise)}</OnboardingGuard>} />
        <Route path="procvicovani/*" element={<PracticeRoutes />} />
        <Route path="doucovani/:subjectSlug/:tutoringId" element={<TutoringPracticeGuard />} />
      </Route>

      <Route path="/*" element={<LayoutSimple />}>
        <Route path="onboarding/*" element={<OnboardingGuard>{lazyRoute(OnboardingRoutes)}</OnboardingGuard>} />
      </Route>

      <Route
        path="cviceni/*"
        element={
          <ExerciseSessionArea key={Math.random()}>
            <ExerciseRoutes />
          </ExerciseSessionArea>
        }
      />

      <Route path="procvicovani/mix" element={<MixedPractice />} />
      <Route path="procvicovani/mhd" element={<MhdPractice />} />

      <Route path="/*" element={<LayoutSimpleFluid />}>
        <Route path="procvicovani/vysledky" element={<PracticeResults />} />
        <Route
          path="onboarding/priklad-vysledky"
          element={<OnboardingGuard>{lazyRoute(OnboardingResults)}</OnboardingGuard>}
        />
        <Route path="odeslani-uciteli/:userAssignmentId" element={<TeacherEvaluationUpload />} />
      </Route>

      {/* DEV ROUTES START */}
      {devPages && (
        <>
          <Route path="/*" element={<Layout />}>
            <Route path="dev/*" element={<devPages.Routes />} />
          </Route>

          <Route path="/*" element={<LayoutSimple />}>
            <Route path="dev/cviceni/zacatek_procvicovani" element={<devPages.Exercise01 />} />
            <Route path="dev/cviceni/zacatek_procvicovani_2a" element={<devPages.Exercise02 />} />
          </Route>

          <Route path="/*" element={<LayoutSimpleWide />}>
            <Route path="dev/cviceni/priklad_slovni_uloha_1" element={<devPages.Exercise03 />} />
            <Route path="dev/cviceni/priklad_slovni_uloha_1_overeno" element={<devPages.Exercise04 />} />
            <Route path="dev/cviceni/priklad_slovni_uloha_3a" element={<devPages.Exercise05 />} />
            <Route path="dev/cviceni/priklad_slovni_uloha_2_overeno" element={<devPages.Exercise06 />} />
            <Route path="dev/cviceni/priklad_rysovani_odpovedi" element={<devPages.Exercise07 />} />
            <Route path="dev/cviceni/priklad_modaly" element={<devPages.Exercise08 />} />
            <Route path="dev/cviceni/vyhodnoceni_1" element={<devPages.Exercise09 />} />
            <Route path="dev/cviceni/vyhodnoceni_2" element={<devPages.Exercise10 />} />
          </Route>

          <Route path="/*" element={<LayoutSimpleFluid />}>
            <Route path="dev/vysledky/tabs" element={<devPages.Results01 />} />
            <Route path="dev/vysledky/tabs-errors" element={<devPages.Results02 />} />
          </Route>
        </>
      )}
      {/* DEV ROUTES END */}

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  </Suspense>
);

export default UserRouter;
